/**
 * Pixel Registry
 *
 * Central registry for managing pixel configurations and adapters.
 * Provides methods to query which pixels are active and retrieve their adapters.
 *
 * @since 1.51.0
 */

(function (wpm, $, undefined) {

	/**
	 * Pixel Registry namespace
	 */
	wpm.pixelRegistry = {

		/**
		 * Get all active pixels (both browser-only and S2S-capable)
		 *
		 * Queries each registered adapter's isActive function to determine
		 * if the pixel should be loaded based on wpmDataLayer configuration.
		 *
		 * @returns {string[]} Array of active pixel names
		 */
		getActivePixels() {
			const activePixels = [];

			if (!wpm.pixelCapabilities) {
				return activePixels;
			}

			// Check each registered pixel's isActive function
			for (const [pixelName, capabilities] of Object.entries(wpm.pixelCapabilities)) {
				if (typeof capabilities.isActive === "function") {
					try {
						if (capabilities.isActive()) {
							activePixels.push(pixelName);
						}
					} catch (e) {
						pmw.console.error(`Error checking if ${pixelName} is active:`, e);
					}
				}
			}

			return activePixels;
		},

		/**
		 * Get browser-only pixels (pixels that don't support S2S)
		 *
		 * @returns {string[]} Array of browser-only pixel names
		 */
		getActiveBrowserOnlyPixels() {
			const browserPixels = [];
			const activePixels  = this.getActivePixels();

			for (const pixelName of activePixels) {
				// If pixel doesn't support S2S, it's browser-only
				if (wpm.pixelCapabilities?.[pixelName]?.supportsS2S !== true) {
					browserPixels.push(pixelName);
				}
			}

			return browserPixels;
		},

		/**
		 * Get S2S-capable pixels
		 *
		 * Returns pixels that declared supportsS2S capability when registering.
		 * These pixels ALWAYS receive the pmw:s2s:* events to fire their browser pixels (fbq, ttq, etc.),
		 * regardless of whether S2S is actually enabled. They handle their own S2S logic internally.
		 *
		 * @returns {string[]} Array of S2S pixel names
		 */
		getActiveS2SPixels() {
			const s2sPixels    = [];
			const activePixels = this.getActivePixels();

			// Check each active pixel's capabilities
			for (const pixelName of activePixels) {
				// Check if this pixel declared supportsS2S capability when registering
				if (wpm.pixelCapabilities?.[pixelName]?.supportsS2S === true) {
					s2sPixels.push(pixelName);
				}
			}

			return s2sPixels;
		},

		/**
		 * Get adapter for a specific pixel
		 *
		 * @param {string} pixelName - The pixel name (e.g., 'facebook', 'google_ads')
		 * @returns {object|null} The adapter object or null if not found
		 */
		getAdapter(pixelName) {
			if (!wpm.adapters) {
				return null;
			}

			return wpm.adapters[pixelName] || null;
		},
	};

}(window.wpm = window.wpm || {}, jQuery));
